<script setup lang="ts">
import { ROLE } from "core/interfaces";
import { useField, useForm, useIsFormDirty, useIsFormValid } from "vee-validate";
import * as yup from "yup";

import { ALERT_STATUS } from "~/common/interfaces/alert-status.enum";
import type { CreateInviteDto } from "~/common/interfaces/dto/invite/create-invite.dto";
import { useAlertStore } from "~/stores/alert";
import { useInviteStore } from "~/stores/invite";
import { useModalStore } from "~/stores/modal";

const { t } = useI18n();
const alertStore = useAlertStore();
const inviteStore = useInviteStore();
const { teamModalInvite } = useModalStore();
const { errors, handleSubmit } = useForm<CreateInviteDto>({
	validationSchema: yup.object({
		email: yup.string()
			.required(t("INPUT.email.error.required"))
			.email(t("INPUT.email.error.email")),
		roles: yup.array()
			.required(t("INPUT.roles.error.required"))
			.min(1, t("INPUT.roles.error.min"))
	})
});
const isFormDirty = useIsFormDirty();
const isFormValid = useIsFormValid();

const onSubmit = handleSubmit(async (dto: CreateInviteDto) => {
	const { data } = await inviteStore.createOne(dto);

	teamModalInvite.closeModal();

	alertStore.addAlert({
		title: t("COMPONENTS.TEAM.MODAL_INVITE.alert.title"),
		description: data,
		status: ALERT_STATUS.SUCCESS
	});
});

const { value: roles } = useField<ROLE[]>("roles");
const { value: email } = useField<string>("email");
</script>

<template>
	<AppModal
		:title="$t('COMPONENTS.TEAM.MODAL_INVITE.title')"
		:close-button-text="$t('COMMON.cancel')"
		:submit-button-text="$t('COMMON.invite')"
		:submit-button-active="isFormValid && isFormDirty"
		@close="teamModalInvite.closeModal"
		@submit="onSubmit"
	>
		<span class="font-semibold text-base">
			{{ $t('COMMON.generalInfo') }}
		</span>
		<div class="mt-4 flex flex-col gap-y-2">
			<AppInput
				v-model="email"
				name="email"
				type="email"
				:title="$t('COMMON.email')"
				:placeholder="$t('INPUT.PLACEHOLDER.email')"
				:error="errors.email"
				required
			/>
			<AppInputTag
				v-model="roles"
				:title="$t('INPUT.roles.title')"
				:placeholder="Object.values(ROLE).join(', ')"
				:white-list="Object.values(ROLE)"
				:error="errors.roles"
				required
			/>
		</div>
	</AppModal>
</template>
